import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  civilDateInZone,
  resolveRollingDateToken,
  zonedEndOfDayIso,
  zonedStartOfDayIso,
} from '@center-platform/spheres/filters';
import { viewerTimeZoneOrDefault } from '@center-technologies/shared-api-clients';
import type { FilterExpression, PerspectiveFilter, UnifiedFilters } from 'types/filters';
import type { DataGridColumn } from '../components/atoms/DataGrid/DataGridAtom';
import type { PerspectiveFilterState } from '../components/molecules/PerspectiveFilter/PerspectiveFilterMolecule';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseColumnFiltersOptions {
  /** Unique key for localStorage persistence */
  settingsKey: string;
  /** Column definitions — used to discard orphaned filters on mount */
  columns: DataGridColumn[];
}

export interface UseColumnFiltersResult {
  /** Full UnifiedFilters object */
  unifiedFilters: UnifiedFilters;
  /** Convenience: active property filters */
  propertyFilters: FilterExpression[];
  /** Convenience: active perspective filters */
  perspectiveFilters: PerspectiveFilter[];
  /** Set or update a property filter for a column */
  setColumnFilter: (field: string, expression: FilterExpression) => void;
  /** Set or update a perspective filter */
  setPerspectiveFilter: (perspectiveSystemName: string, assignees: string[]) => void;
  /** Remove a property or perspective filter by field / perspectiveSystemName */
  removeColumnFilter: (field: string) => void;
  /** Get the active property filter for a column */
  getColumnFilter: (field: string) => FilterExpression | undefined;
  /** Clear all column filters */
  clearAllFilters: () => void;
  /** Whether any filter is active */
  hasActiveFilters: boolean;
  /** Apply all active filters to a dataset */
  applyFilters: <T>(data: T[], perspectiveResolver?: (item: T, systemName: string) => string[]) => T[];
  /** Perspective filters converted to PerspectiveFilterState[] for FilterChipsDisplayMolecule */
  perspectiveFiltersAsState: PerspectiveFilterState[];
}

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

const STORAGE_PREFIX = 'datalist-column-filters-';

function getStorageKey(settingsKey: string): string {
  return `${STORAGE_PREFIX}${settingsKey}`;
}

function loadFromStorage(settingsKey: string): UnifiedFilters | null {
  try {
    const stored = localStorage.getItem(getStorageKey(settingsKey));
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Silently discard corrupt data
  }
  return null;
}

function saveToStorage(settingsKey: string, filters: UnifiedFilters): void {
  try {
    localStorage.setItem(getStorageKey(settingsKey), JSON.stringify(filters));
  } catch {
    // Silently ignore storage errors
  }
}

// ---------------------------------------------------------------------------
// Filter-application helpers (pure functions)
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

type RangeBound = number | string;

/** Case-insensitive substring match; a missing value never matches. */
function matchesContains(raw: unknown, value: unknown): boolean {
  if (raw == null) return false;
  return String(raw).toLowerCase().includes(String(value ?? '').toLowerCase());
}

/** An `in`/`not_in` filter with no values selected constrains nothing. */
function hasValueList(values: unknown): values is unknown[] {
  return Array.isArray(values) && values.length > 0;
}

/** String coercion handles numeric fields (e.g. urgency: 5) vs string enum values ('5'). */
function valueListIncludes(values: unknown[], raw: unknown): boolean {
  return values.some((v) => v === raw || String(v) === String(raw));
}

/**
 * This evaluator runs entirely client-side against already-loaded rows, so it
 * resolves rolling-date tokens itself rather than depending on a server round
 * trip. It MUST agree with the server about what each token means, or a column
 * filtered in the browser and the same filter executed against the graph select
 * different rows with nothing to say so — the same class of silent divergence
 * the variable-binding cache key guards against.
 *
 * Agreement is structural, not by imitation: the token grammar and the calendar
 * arithmetic come from `@center-platform/spheres/filters` — the one module
 * `resolveFilterVariables.ts` resolves through — and the two functions below
 * mirror its `resolveValue` / `resolveBound` split exactly.
 *
 * The zone comes from `viewerTimeZoneOrDefault()`, the same resolution the API
 * client puts on the wire as `x-center-timezone`. So the browser and the server
 * decide "today" from one value, and both fall back to UTC together when the
 * runtime cannot name a zone.
 */
function todayInViewerZone(): string {
  return civilDateInZone(new Date(), viewerTimeZoneOrDefault());
}

/**
 * A scalar operand: a rolling-date token becomes the bare `YYYY-MM-DD` civil
 * date, exactly as `resolveFilterVariables.ts`'s `resolveValue` produces for a
 * non-`between` date comparison. Anything else is returned untouched — a `null`
 * from the resolver is a REFUSAL ("not a rolling date"), never "treat it as
 * today", so a malformed token (`@today-07d`) stays a literal that matches
 * nothing rather than silently becoming a date.
 *
 * Identity tokens (`@me`, `@parent`, `@center`) are not resolved here: this
 * evaluator has no identity context, and leaving them literal keeps them
 * matching nothing rather than widening.
 */
function resolveRollingValue(value: unknown): unknown {
  if (typeof value !== 'string' || !value.startsWith('@')) return value;
  return resolveRollingDateToken(value, todayInViewerZone()) ?? value;
}

/**
 * A `between` bound needs the whole calendar DAY, not the instant a bare
 * `YYYY-MM-DD` names: as `min` a bare date excludes that day's earlier rows,
 * as `max` its later ones — either way a same-day row is wrongly dropped.
 * The day's edges are the exact UTC instants at which that civil day begins and
 * ends IN THE VIEWER'S ZONE, which is not UTC midnight for anyone outside UTC.
 * Mirrors `resolveFilterVariables.ts`'s `resolveBound`.
 */
function resolveRollingBound(bound: RangeBound, edge: 'start' | 'end'): RangeBound {
  if (typeof bound !== 'string') return bound;
  const civilDate = resolveRollingDateToken(bound, todayInViewerZone());
  if (civilDate === null) return bound;
  const timeZone = viewerTimeZoneOrDefault();
  return edge === 'start'
    ? zonedStartOfDayIso(civilDate, timeZone)
    : zonedEndOfDayIso(civilDate, timeZone);
}

/** A string parses as a date; a number is already epoch ms (or a plain numeric value). */
function toComparableMs(value: RangeBound): number {
  return typeof value === 'string' ? new Date(value).getTime() : value;
}

/**
 * A date column's actual value is a stored ISO string; a numeric column's is
 * already a number. A bound is a number for a numeric column, or the canonical
 * ISO 8601 UTC string for a date column (Phase 7a, EAINT-931) — or, read-back
 * only, a legacy epoch-ms number a pre-7a row already persisted. Both sides
 * normalise the same way, so a numeric bound against a numeric actual, or a
 * date bound (ISO or legacy ms) against a date actual, compare correctly.
 */
function matchesBetween(raw: unknown, min: RangeBound | undefined, max: RangeBound | undefined): boolean {
  if (raw == null) return false;
  const actual = toComparableMs(raw as RangeBound);
  const lower = min === undefined ? -Infinity : toComparableMs(resolveRollingBound(min, 'start'));
  const upper = max === undefined ? Infinity : toComparableMs(resolveRollingBound(max, 'end'));
  return actual >= lower && actual <= upper;
}

/** Whether a date value falls between `days` days ago and now. */
function isWithinLastDays(raw: unknown, days: number): boolean {
  if (raw == null) return false;
  const date = new Date(raw as string | number);
  const now = new Date();
  const since = new Date(now.getTime() - days * DAY_MS);
  return date >= since && date <= now;
}

/** Whether a date value falls between now and `days` days ahead — the mirror of {@link isWithinLastDays}. */
function isWithinNextDays(raw: unknown, days: number): boolean {
  if (raw == null) return false;
  const date = new Date(raw as string | number);
  const now = new Date();
  const until = new Date(now.getTime() + days * DAY_MS);
  return date >= now && date <= until;
}

/** Symmetric (past OR future) — `date_within_days`' absolute day difference. */
function isDateWithinDays(raw: unknown, days: number): boolean {
  if (raw == null) return false;
  const ms = new Date(raw as string | number).getTime();
  if (Number.isNaN(ms)) return false;
  return Math.abs(Date.now() - ms) / DAY_MS <= days;
}

/** A finite integer >= 1, or null — an invalid day count never matches (fail closed), never coerces. */
function dayCount(value: unknown): number | null {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 ? n : null;
}

/** Absent, null, empty string or empty array — the emptiness `is_empty` answers. */
function isEmptyValue(raw: unknown): boolean {
  return raw === undefined || raw === null || raw === '' || (Array.isArray(raw) && raw.length === 0);
}

/**
 * Ordering for a column value against a filter operand: negative, zero or
 * positive, or null when the pair has no order and the clause must fail closed.
 *
 * Both sides are coerced to numbers when both parse — the value editor writes an
 * `<input type="number">`'s STRING, so a numeric column compares a number
 * against `'5'` and must still order numerically. Otherwise both compare as
 * strings, which for the canonical ISO 8601 UTC date representation (Phase 7a,
 * EAINT-931) is chronological order.
 */
function compareOrdering(raw: unknown, value: unknown): number | null {
  if (raw == null || value == null || value === '') return null;
  const rawNumber = typeof raw === 'number' ? raw : Number(raw);
  const valueNumber = typeof value === 'number' ? value : Number(value);
  if (Number.isFinite(rawNumber) && Number.isFinite(valueNumber)) return rawNumber - valueNumber;
  const rawText = String(raw);
  const valueText = String(value);
  if (rawText === valueText) return 0;
  return rawText < valueText ? -1 : 1;
}

/** The four ordering operators, with an unorderable pair failing closed for all of them. */
function matchesOrdering(raw: unknown, filter: FilterExpression): boolean {
  const order = compareOrdering(raw, filter.value);
  if (order === null) return false;
  switch (filter.operator) {
    case 'greater_than':
      return order > 0;
    case 'greater_than_equals':
      return order >= 0;
    case 'less_than':
      return order < 0;
    default:
      return order <= 0;
  }
}

/**
 * The expression with its rolling-date tokens resolved — `value` and each entry
 * of `values`, the same fields `resolveFilterVariables.ts`'s `resolveExpression`
 * resolves. `min`/`max` are NOT resolved here: a bound needs the whole day, so
 * `matchesBetween` resolves it through `resolveRollingBound` instead, exactly
 * as the server's `resolveBound` is separate from its `resolveValue`.
 *
 * Returns the same object when nothing changed, so the common case allocates
 * nothing.
 */
function resolveRollingExpression(filter: FilterExpression): FilterExpression {
  const value = resolveRollingValue(filter.value);
  const values = filter.values?.map(resolveRollingValue);
  if (value === filter.value && (values === undefined || values.every((v, i) => v === filter.values?.[i]))) {
    return filter;
  }
  return { ...filter, value, values };
}

function matchesPropertyFilter<T>(item: T, unresolved: FilterExpression): boolean {
  const filter = resolveRollingExpression(unresolved);
  const raw = (item as Record<string, unknown>)[filter.property];
  // Every operator `ColumnFilterHeaderAtom` can reach through
  // `getFilterOperatorOptions` must have a real branch here. A missing one used
  // to fall through to `true`, which does not skip the clause — it WIDENS the
  // result set to every row while the header still shows the filter as active.
  switch (filter.operator) {
    case 'contains':
      return matchesContains(raw, filter.value);
    case 'not_contains':
      // An absent value is false, never true — mirrors `compareValues`.
      return raw != null && !matchesContains(raw, filter.value);
    case 'equals':
      return raw === filter.value;
    case 'not_equals':
      return raw !== filter.value;
    case 'starts_with':
      return raw != null && String(raw).toLowerCase().startsWith(String(filter.value ?? '').toLowerCase());
    case 'ends_with':
      return raw != null && String(raw).toLowerCase().endsWith(String(filter.value ?? '').toLowerCase());
    case 'in':
      return !hasValueList(filter.values) || valueListIncludes(filter.values, raw);
    case 'not_in':
      return !hasValueList(filter.values) || !valueListIncludes(filter.values, raw);
    case 'contains_all':
      return (
        hasValueList(filter.values) &&
        Array.isArray(raw) &&
        filter.values.every((v) => valueListIncludes([v], raw))
      );
    case 'greater_than':
    case 'greater_than_equals':
    case 'less_than':
    case 'less_than_equals':
      return matchesOrdering(raw, filter);
    case 'between':
      return matchesBetween(raw, filter.min, filter.max);
    case 'is_empty':
      return isEmptyValue(raw);
    case 'is_not_empty':
      return !isEmptyValue(raw);
    case 'is_true':
      return raw === true;
    case 'is_false':
      return raw === false;
    case 'exists':
      return raw !== undefined && raw !== null;
    case 'not_exists':
      return raw === undefined || raw === null;
    case 'within_last_days': {
      const n = dayCount(filter.value);
      return n !== null && isWithinLastDays(raw, n);
    }
    case 'within_next_days': {
      const n = dayCount(filter.value);
      return n !== null && isWithinNextDays(raw, n);
    }
    case 'date_within_days': {
      const n = Number(filter.value);
      return Number.isFinite(n) && n >= 0 && isDateWithinDays(raw, n);
    }
    case 'last_7_days':
      return isWithinLastDays(raw, 7);
    case 'last_30_days':
      return isWithinLastDays(raw, 30);
    default:
      // Never-widen: an operator outside the canonical union matches nothing
      // (/standards/shared/filtering.md, "the unknown-operator invariant").
      return false;
  }
}

function matchesPerspectiveFilter<T>(
  item: T,
  filter: PerspectiveFilter,
  resolver: (item: T, systemName: string) => string[],
): boolean {
  if (filter.assignees.length === 0) return true;
  const itemAssignees = resolver(item, filter.perspectiveSystemName);

  if (filter.operator === 'in') {
    return filter.assignees.some((id) => itemAssignees.includes(id));
  }
  // not_in
  return !filter.assignees.some((id) => itemAssignees.includes(id));
}

// ---------------------------------------------------------------------------
// Conversion: PerspectiveFilter[] → PerspectiveFilterState[]
// ---------------------------------------------------------------------------

function convertToPerspectiveFilterState(filters: PerspectiveFilter[]): PerspectiveFilterState[] {
  return filters.map((filter) => ({
    perspectiveSystemName: filter.perspectiveSystemName,
    includeInHierarchy: filter.operator !== 'not_in',
    assigneeFilter: {
      filterType: filter.assignees.length > 0 ? 'specific' : 'any',
      assigneeIds: filter.assignees,
      selectedAssignees: [],
    },
  }));
}

// ---------------------------------------------------------------------------
// Orphan cleanup
// ---------------------------------------------------------------------------

function cleanOrphanedFilters(stored: UnifiedFilters, columns: DataGridColumn[]): UnifiedFilters {
  const columnFields = new Set(columns.map((c) => c.field));
  const perspectiveNames = new Set(
    columns.filter((c) => c.filterPerspectiveSystemName).map((c) => c.filterPerspectiveSystemName!),
  );

  return {
    propertyFilters: (stored.propertyFilters ?? []).filter((f) => columnFields.has(f.property)),
    perspectiveFilters: (stored.perspectiveFilters ?? []).filter((f) =>
      perspectiveNames.has(f.perspectiveSystemName),
    ),
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const EMPTY_FILTERS: UnifiedFilters = { propertyFilters: [], perspectiveFilters: [] };

export function useColumnFilters({
  settingsKey,
  columns,
}: UseColumnFiltersOptions): UseColumnFiltersResult {
  const [filters, setFilters] = useState<UnifiedFilters>(() => {
    const stored = loadFromStorage(settingsKey);
    if (stored) {
      return cleanOrphanedFilters(stored, columns);
    }
    return EMPTY_FILTERS;
  });

  // Persist on every change
  useEffect(() => {
    saveToStorage(settingsKey, filters);
  }, [settingsKey, filters]);

  // Convenience accessors
  const propertyFilters = useMemo(() => filters.propertyFilters ?? [], [filters]);
  const perspectiveFilters = useMemo(() => filters.perspectiveFilters ?? [], [filters]);

  const hasActiveFilters = useMemo(
    () => propertyFilters.length > 0 || perspectiveFilters.length > 0,
    [propertyFilters, perspectiveFilters],
  );

  // --- Property filter CRUD ---

  const setColumnFilter = useCallback((field: string, expression: FilterExpression) => {
    // Treat empty text value as "remove filter"
    if (
      (expression.operator === 'contains' || expression.operator === 'equals') &&
      (expression.value === '' || expression.value == null)
    ) {
      setFilters((prev) => ({
        ...prev,
        propertyFilters: (prev.propertyFilters ?? []).filter((f) => f.property !== field),
      }));
      return;
    }

    setFilters((prev) => {
      const existing = (prev.propertyFilters ?? []).filter((f) => f.property !== field);
      return {
        ...prev,
        propertyFilters: [...existing, { ...expression, property: field }],
      };
    });
  }, []);

  const removeColumnFilter = useCallback((field: string) => {
    setFilters((prev) => ({
      ...prev,
      propertyFilters: (prev.propertyFilters ?? []).filter((f) => f.property !== field),
      perspectiveFilters: (prev.perspectiveFilters ?? []).filter(
        (f) => f.perspectiveSystemName !== field,
      ),
    }));
  }, []);

  const getColumnFilter = useCallback(
    (field: string): FilterExpression | undefined => {
      return propertyFilters.find((f) => f.property === field);
    },
    [propertyFilters],
  );

  // --- Perspective filter CRUD ---

  const setPerspectiveFilter = useCallback(
    (perspectiveSystemName: string, assignees: string[]) => {
      // Empty assignees = remove filter
      if (assignees.length === 0) {
        setFilters((prev) => ({
          ...prev,
          perspectiveFilters: (prev.perspectiveFilters ?? []).filter(
            (f) => f.perspectiveSystemName !== perspectiveSystemName,
          ),
        }));
        return;
      }

      setFilters((prev) => {
        const existing = (prev.perspectiveFilters ?? []).filter(
          (f) => f.perspectiveSystemName !== perspectiveSystemName,
        );
        return {
          ...prev,
          perspectiveFilters: [
            ...existing,
            { perspectiveSystemName, operator: 'in' as const, assignees },
          ],
        };
      });
    },
    [],
  );

  // --- Shared ---

  const clearAllFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
  }, []);

  const applyFilters = useCallback(
    <T>(data: T[], perspectiveResolver?: (item: T, systemName: string) => string[]): T[] => {
      if (!hasActiveFilters) return data;

      return data.filter((item) => {
        // All property filters must match (AND)
        for (const pf of propertyFilters) {
          if (!matchesPropertyFilter(item, pf)) return false;
        }
        // All perspective filters must match (AND)
        if (perspectiveResolver) {
          for (const pf of perspectiveFilters) {
            if (!matchesPerspectiveFilter(item, pf, perspectiveResolver)) return false;
          }
        }
        return true;
      });
    },
    [hasActiveFilters, propertyFilters, perspectiveFilters],
  );

  // --- Chip display helper ---

  const perspectiveFiltersAsState = useMemo(
    () => convertToPerspectiveFilterState(perspectiveFilters),
    [perspectiveFilters],
  );

  return {
    unifiedFilters: filters,
    propertyFilters,
    perspectiveFilters,
    setColumnFilter,
    setPerspectiveFilter,
    removeColumnFilter,
    getColumnFilter,
    clearAllFilters,
    hasActiveFilters,
    applyFilters,
    perspectiveFiltersAsState,
  };
}
